import { useState, useEffect, useCallback } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Switch, FormControlLabel, MenuItem,
    Skeleton, Tooltip, Alert, FormControl, InputLabel, Select,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import { AppConfig } from "Shared/Config/AppConfig";

interface AppVersionData {
    seqId: number;
    platform: string;
    version: string;
    minVersion: string;
    updateType: string;
    title: string | null;
    message: string | null;
    storeUrl: string | null;
    isActive: boolean;
    crtDt: string;
    edtDt: string | null;
}

interface StoreVersions {
    iosVersion: string | null;
    androidVersion: string | null;
    iosError: string | null;
    androidError: string | null;
}

const EMPTY_FORM: Omit<AppVersionData, "seqId" | "crtDt" | "edtDt"> = {
    platform: "ios",
    version: "",
    minVersion: "",
    updateType: "OPTIONAL",
    title: "",
    message: "",
    storeUrl: "",
    isActive: true,
};

function AppVersionScreen(): JSX.Element {
    const [versions, setVersions] = useState<AppVersionData[]>([]);
    const [loading, setLoading] = useState(false);
    const [storeVersions, setStoreVersions] = useState<StoreVersions | null>(null);
    const [storeLoading, setStoreLoading] = useState(false);
    const [platformCodes, setPlatformCodes] = useState<{ code: string; name: string }[]>([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<AppVersionData | null>(null);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<AppVersionData | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmMsg, setConfirmMsg] = useState("");
    const [formError, setFormError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await request("AppVersion.getAll", {});
            setVersions(res?.data ?? []);
        } catch (err) {
            console.error("앱 버전 조회 실패", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadStoreVersions = useCallback(async () => {
        setStoreLoading(true);
        try {
            const res = await request("AppVersion.storeVersions", {});
            setStoreVersions(res?.data ?? null);
        } catch (err) {
            console.error("스토어 버전 조회 실패", err);
        } finally {
            setStoreLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        loadStoreVersions();
        // 공통코드 DEVICE_OS 그룹 로드
        request("CommonCode.getByGroup", { pathParams: { groupCode: "DEVICE_OS" } })
            .then((res) => {
                const codes = (res?.data ?? []).map((c: any) => ({ code: c.code, name: c.name }));
                setPlatformCodes(codes);
            })
            .catch(() => {
                // 실패 시 fallback
                setPlatformCodes([{ code: "ios", name: "iOS" }, { code: "android", name: "Android" }]);
            });
    }, [load, loadStoreVersions]);

    const getStoreVersion = (platform: string) => {
        if (!storeVersions) return "";
        return platform === "ios"
            ? (storeVersions.iosVersion ?? "")
            : (storeVersions.androidVersion ?? "");
    };

    const openAdd = () => {
        const storeVer = getStoreVersion(EMPTY_FORM.platform);
        const storeUrl = AppConfig.storeUrls[EMPTY_FORM.platform as keyof typeof AppConfig.storeUrls] ?? "";
        setEditing(null);
        setForm({ ...EMPTY_FORM, version: storeVer, minVersion: storeVer, storeUrl });
        setFormError("");
        setModalOpen(true);
    };

    const openEdit = (v: AppVersionData) => {
        setEditing(v);
        setForm({
            platform: v.platform,
            version: v.version,
            minVersion: v.minVersion,
            updateType: v.updateType,
            title: v.title ?? "",
            message: v.message ?? "",
            storeUrl: v.storeUrl ?? "",
            isActive: v.isActive,
        });
        setModalOpen(true);
    };

    const compareVersions = (a: string, b: string): number => {
        const pa = a.split(".").map(Number);
        const pb = b.split(".").map(Number);
        const len = Math.max(pa.length, pb.length);
        for (let i = 0; i < len; i++) {
            const na = pa[i] ?? 0;
            const nb = pb[i] ?? 0;
            if (na !== nb) return na < nb ? -1 : 1;
        }
        return 0;
    };

    /** 확인 다이얼로그에서 "진행" 클릭 시 실제 저장 */
    const handleConfirmProceed = async () => {
        setConfirmOpen(false);
        await doSave();
    };

    /** 실제 저장 로직 */
    const doSave = async () => {
        setSaving(true);
        try {
            if (editing) {
                await request("AppVersion.update", {
                    pathParams: { seqId: String(editing.seqId) },
                    paramz: { ...form, seqId: editing.seqId },
                });
            } else {
                await request("AppVersion.create", { paramz: form });
            }
            setModalOpen(false);
            load();
        } catch (err) {
            console.error("저장 실패", err);
            alert("저장 중 오류가 발생했습니다.");
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (!form.version || !form.minVersion) {
            setFormError("버전과 최소 버전은 필수 항목입니다.");
            return;
        }

        // 신규 등록 시: 동일 플랫폼+버전이 이미 존재하면 차단
        if (!editing) {
            const duplicate = versions.find(
                (v) => v.platform === form.platform && v.version === form.version
            );
            if (duplicate) {
                setFormError(`이미 등록된 버전입니다. (${form.platform} v${form.version})`);
                return;
            }
        }

        setFormError("");

        // 신규 등록 시: PlayStore/AppStore 실배포 버전과 다르면 확인 다이얼로그
        if (!editing) {
            const storeVer = getStoreVersion(form.platform);
            if (storeVer) {
                const diff = compareVersions(form.version, storeVer);
                if (diff !== 0) {
                    const direction = diff > 0
                        ? `스토어 버전(${storeVer})보다 높은 버전(${form.version})을 등록합니다.\n기존 활성 버전은 자동으로 비활성화됩니다.`
                        : `⚠️ 스토어 버전(${storeVer})보다 낮은 버전(${form.version})을 등록합니다.\n다운그레이드 시 기존 활성 버전이 비활성화됩니다.`;
                    setConfirmMsg(`${direction}\n\n그래도 진행하시겠습니까?`);
                    setConfirmOpen(true);
                    return;
                }
            }
        }

        await doSave();
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await request("AppVersion.delete", {
                pathParams: { seqId: String(deleteTarget.seqId) },
                paramz: {},
            });
            setDeleteTarget(null);
            load();
        } catch (err) {
            console.error("삭제 실패", err);
            alert("삭제 중 오류가 발생했습니다.");
        }
    };

    const platformLabel = (p: string) => p === "ios" ? "iOS" : "Android";
    const platformIcon = (p: string) => p === "ios" ? "apple" : "android";
    const platformColor = (p: string): "info" | "success" => p === "ios" ? "info" : "success";

    // 현재 활성 버전 찾기
    const activeIos = versions.find(v => v.isActive && v.platform === "ios");
    const activeAndroid = versions.find(v => v.isActive && v.platform === "android");

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                {/* 헤더 카드 */}
                <Card>
                    <MDBox p={3}>
                        {/* 타이틀 + 버튼 */}
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
                            <MDBox display="flex" alignItems="center">
                                <Icon fontSize="medium">system_update</Icon>
                                <MDTypography ml={1} variant="h5" fontWeight="medium">
                                    앱 버전관리
                                </MDTypography>
                            </MDBox>
                            <MDBox display="flex" gap={1}>
                                <Tooltip title="스토어 버전 새로고침">
                                    <span>
                                        <MDButton
                                            variant="outlined"
                                            color="secondary"
                                            iconOnly
                                            onClick={loadStoreVersions}
                                            disabled={storeLoading}
                                        >
                                            <Icon>{storeLoading ? "hourglass_empty" : "refresh"}</Icon>
                                        </MDButton>
                                    </span>
                                </Tooltip>
                                <MDButton variant="gradient" color="info" onClick={openAdd}>
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>add</Icon>
                                    버전 추가
                                </MDButton>
                            </MDBox>
                        </MDBox>

                        {/* 스토어 현재 버전 배너 */}
                        <Grid container spacing={2}>
                            {/* App Store */}
                            <Grid item xs={12} sm={6}>
                                <MDBox
                                    border="1px solid"
                                    borderColor={storeVersions?.iosVersion ? "rgba(73,163,241,0.4)" : "rgba(200,200,200,0.3)"}
                                    borderRadius="lg"
                                    p={2}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    sx={{ backgroundColor: "rgba(73,163,241,0.06)" }}
                                >
                                    <Icon sx={{ color: "#49a3f1", fontSize: "2rem !important" }}>apple</Icon>
                                    <MDBox flex={1}>
                                        <MDTypography variant="caption" color="secondary" display="block">
                                            App Store 배포 버전
                                        </MDTypography>
                                        {storeLoading ? (
                                            <Skeleton width={80} height={28} />
                                        ) : storeVersions?.iosVersion ? (
                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                <MDTypography variant="h5" fontWeight="bold" color="info">
                                                    v{storeVersions.iosVersion}
                                                </MDTypography>
                                                {activeIos && (
                                                    <Chip
                                                        label={activeIos.version === storeVersions.iosVersion ? "최신" : `DB: v${activeIos.version}`}
                                                        size="small"
                                                        color={activeIos.version === storeVersions.iosVersion ? "success" : "warning"}
                                                        sx={{ fontSize: "0.68rem", height: 20 }}
                                                    />
                                                )}
                                            </MDBox>
                                        ) : (
                                            <MDTypography
                                                variant="body2"
                                                color={storeVersions?.iosError ? "warning" : "text"}
                                            >
                                                {storeVersions?.iosError ?? (storeLoading ? "조회 중..." : "버전 정보 없음")}
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                    {storeVersions?.iosVersion && (
                                        <a
                                            href="https://apps.apple.com/kr/app/id6756594702"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: "none" }}
                                        >
                                            <Icon sx={{ color: "#49a3f1", fontSize: "1.2rem !important", cursor: "pointer" }}>
                                                open_in_new
                                            </Icon>
                                        </a>
                                    )}
                                </MDBox>
                            </Grid>

                            {/* Google Play */}
                            <Grid item xs={12} sm={6}>
                                <MDBox
                                    border="1px solid"
                                    borderColor={storeVersions?.androidVersion ? "rgba(76,175,80,0.4)" : "rgba(200,200,200,0.3)"}
                                    borderRadius="lg"
                                    p={2}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    sx={{ backgroundColor: "rgba(76,175,80,0.06)" }}
                                >
                                    <Icon sx={{ color: "#4CAF50", fontSize: "2rem !important" }}>android</Icon>
                                    <MDBox flex={1}>
                                        <MDTypography variant="caption" color="secondary" display="block">
                                            Play Store 배포 버전
                                        </MDTypography>
                                        {storeLoading ? (
                                            <Skeleton width={80} height={28} />
                                        ) : storeVersions?.androidVersion ? (
                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#4CAF50" }}>
                                                    v{storeVersions.androidVersion}
                                                </MDTypography>
                                                {activeAndroid && (
                                                    <Chip
                                                        label={activeAndroid.version === storeVersions.androidVersion ? "최신" : `DB: v${activeAndroid.version}`}
                                                        size="small"
                                                        color={activeAndroid.version === storeVersions.androidVersion ? "success" : "warning"}
                                                        sx={{ fontSize: "0.68rem", height: 20 }}
                                                    />
                                                )}
                                            </MDBox>
                                        ) : (
                                            <MDTypography variant="body2" color="error">
                                                {storeVersions?.androidError ?? "조회 중..."}
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                    {storeVersions?.androidVersion && (
                                        <a
                                            href="https://play.google.com/store/apps/details?id=com.mediinstep.app"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: "none" }}
                                        >
                                            <Icon sx={{ color: "#4CAF50", fontSize: "1.2rem !important", cursor: "pointer" }}>
                                                open_in_new
                                            </Icon>
                                        </a>
                                    )}
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {/* 버전 내역 리스트 */}
                <MDBox mt={5}>
                    {loading ? (
                        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                            <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                        </MDBox>
                    ) : versions.length === 0 ? (
                        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                            <MDTypography variant="h6" color="text">등록된 버전 정보가 없습니다.</MDTypography>
                        </MDBox>
                    ) : (
                        <Grid container spacing={3} alignItems="stretch">
                            {versions.map((v) => (
                                <Grid item xs={12} sm={6} md={4} xl={3} key={v.seqId} sx={{ display: "flex" }}>
                                    <Card sx={{ width: "100%", display: "flex", flexDirection: "column", ...(v.isActive ? { border: "2px solid #4CAF50", boxShadow: "0 0 0 4px rgba(76,175,80,0.12)" } : {}) }}>
                                        {/* 카드 상단 컬러 헤더 */}
                                        <MDBox
                                            mx={2} mt={-3} py={2} px={2}
                                            variant="gradient"
                                            bgColor={v.isActive ? "success" : "secondary"}
                                            borderRadius="lg"
                                            coloredShadow={v.isActive ? "success" : "secondary"}
                                            display="flex" justifyContent="space-between" alignItems="center"
                                        >
                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                <Icon sx={{ color: "#fff" }}>{platformIcon(v.platform)}</Icon>
                                                <MDTypography variant="h6" color="white">
                                                    {platformLabel(v.platform)} v{v.version}
                                                </MDTypography>
                                            </MDBox>
                                            {v.isActive && (
                                                <Chip
                                                    label="현재 버전"
                                                    size="small"
                                                    sx={{ backgroundColor: "#fff", color: "#4CAF50", fontWeight: 700, fontSize: "0.7rem" }}
                                                />
                                            )}
                                        </MDBox>

                                        <MDBox p={2.5} display="flex" flexDirection="column" flexGrow={1}>
                                            {/* 버전 정보 */}
                                            <MDBox display="flex" justifyContent="space-between" mb={1}>
                                                <MDTypography variant="caption" color="secondary">최소 버전</MDTypography>
                                                <MDTypography variant="caption" fontWeight="medium">{v.minVersion}</MDTypography>
                                            </MDBox>
                                            <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                                                <MDTypography variant="caption" color="secondary">업데이트 유형</MDTypography>
                                                <MDBadge
                                                    badgeContent={v.updateType === "MANDATORY" ? "강제" : "선택"}
                                                    color={v.updateType === "MANDATORY" ? "error" : "info"}
                                                    variant="gradient"
                                                    size="xs"
                                                />
                                            </MDBox>

                                            {(v.title || v.message) && (
                                                <>
                                                    <Divider sx={{ my: 1 }} />
                                                    {v.title && (
                                                        <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                                                            {v.title}
                                                        </MDTypography>
                                                    )}
                                                    {v.message && (
                                                        <MDTypography variant="caption" color="secondary" display="block" sx={{ whiteSpace: "pre-line" }}>
                                                            {v.message}
                                                        </MDTypography>
                                                    )}
                                                </>
                                            )}

                                            <Divider sx={{ my: 1.5 }} />
                                            <MDTypography variant="caption" color="secondary" display="block">
                                                등록: {new Date(v.crtDt).toLocaleDateString("ko-KR")}
                                                {v.edtDt && ` · 수정: ${new Date(v.edtDt).toLocaleDateString("ko-KR")}`}
                                            </MDTypography>

                                            {/* 액션 버튼 */}
                                            <MDBox display="flex" justifyContent="flex-end" gap={1} mt="auto" pt={1.5}>
                                                <MDButton
                                                    variant="outlined" color="error" size="small"
                                                    onClick={() => setDeleteTarget(v)}
                                                >
                                                    <Icon fontSize="small">delete</Icon>
                                                </MDButton>
                                            </MDBox>
                                        </MDBox>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </MDBox>
            </MDBox>
            <Footer />

            {/* 등록/수정 모달 */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? "버전 수정" : "버전 추가"}</DialogTitle>
                <DialogContent dividers>
                    <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                        {formError && (
                            <Alert severity="error" onClose={() => setFormError("")}>
                                {formError}
                            </Alert>
                        )}
                        <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { height: "44px" } }}>
                            <InputLabel id="platform-label">플랫폼</InputLabel>
                            <Select
                                labelId="platform-label"
                                label="플랫폼"
                                value={form.platform}
                                onChange={(e) => {
                                    const newPlatform = e.target.value as string;
                                    const storeVer = getStoreVersion(newPlatform);
                                    const storeUrl = AppConfig.storeUrls[newPlatform as keyof typeof AppConfig.storeUrls] ?? "";
                                    setForm(prev => ({
                                        ...prev,
                                        platform: newPlatform,
                                        storeUrl,
                                        ...(editing ? {} : { version: storeVer, minVersion: storeVer }),
                                    }));
                                }}
                            >
                                {platformCodes.length > 0
                                    ? platformCodes.map((p) => (
                                        <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
                                    ))
                                    : [<MenuItem key="ios" value="ios">iOS</MenuItem>, <MenuItem key="android" value="android">Android</MenuItem>]
                                }
                            </Select>
                        </FormControl>
                        <TextField
                            label="현재 버전 *"
                            value={form.version}
                            onChange={(e) => {
                                const v = e.target.value;
                                setForm({ ...form, version: v });
                            }}
                            fullWidth
                            placeholder="예: 1.2.3"
                            error={!editing && !!versions.find(
                                (v) => v.platform === form.platform && v.version === form.version
                            )}
                            helperText={
                                !editing && versions.find(
                                    (v) => v.platform === form.platform && v.version === form.version
                                )
                                    ? "이미 등록된 버전입니다."
                                    : ""
                            }
                        />
                        <TextField
                            label="최소 지원 버전 *"
                            value={form.minVersion}
                            onChange={(e) => setForm({ ...form, minVersion: e.target.value })}
                            fullWidth
                            placeholder="예: 1.0.0"
                            helperText="이 버전 미만은 강제 업데이트 대상"
                        />
                        <MDBox>
                            <MDTypography variant="caption" color="secondary" mb={0.5} display="block">업데이트 유형</MDTypography>
                            <MDBox display="flex" gap={1}>
                                {["OPTIONAL", "MANDATORY"].map((t) => (
                                    <MDButton
                                        key={t}
                                        variant={form.updateType === t ? "gradient" : "outlined"}
                                        color={t === "MANDATORY" ? "error" : "info"}
                                        size="small"
                                        onClick={() => setForm({ ...form, updateType: t })}
                                    >
                                        {t === "MANDATORY" ? "강제 업데이트" : "선택 업데이트"}
                                    </MDButton>
                                ))}
                            </MDBox>
                        </MDBox>
                        <TextField
                            label="업데이트 제목"
                            value={form.title ?? ""}
                            onChange={(e) => setForm({ ...form, title: e.target.value })}
                            fullWidth size="small"
                        />
                        <TextField
                            label="업데이트 내용"
                            value={form.message ?? ""}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            fullWidth size="small"
                            multiline rows={3}
                        />
                        <TextField
                            label="스토어 URL"
                            value={form.storeUrl ?? ""}
                            onChange={(e) => setForm({ ...form, storeUrl: e.target.value })}
                            fullWidth size="small"
                        />

                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setModalOpen(false)} color="secondary">취소</MDButton>
                    <MDButton onClick={handleSave} variant="gradient" color="info" disabled={saving}>
                        {saving ? "저장 중..." : "저장"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* 버전 등록 확인 다이얼로그 */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>버전 등록 확인</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                        {confirmMsg}
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setConfirmOpen(false)} color="secondary">취소</MDButton>
                    <MDButton onClick={handleConfirmProceed} variant="gradient" color="info" disabled={saving}>
                        {saving ? "저장 중..." : "진행"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* 삭제 확인 다이얼로그 */}
            <Dialog open={!!deleteTarget && !deleteTarget.isActive} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>버전 삭제 확인</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2">
                        {deleteTarget && `${platformLabel(deleteTarget.platform)} v${deleteTarget.version}`}을(를) 삭제하시겠습니까?<br />
                        이 작업은 취소할 수 없습니다.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setDeleteTarget(null)} color="secondary">취소</MDButton>
                    <MDButton onClick={handleDelete} variant="gradient" color="error">삭제</MDButton>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default AppVersionScreen;
